import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Webcam from 'react-webcam';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  LinearProgress,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Tooltip,
  Fade,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Badge,
  Fab,
  TextField,
  MenuItem,
  ButtonGroup
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  FlashOn as FlashOnIcon,
  FlashOff as FlashOffIcon,
  Cameraswitch as CameraSwitchIcon,
  Psychology as PsychologyIcon,
  ShoppingCart as ShoppingCartIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  AddShoppingCart as AddShoppingCartIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';

// Create an Axios instance with default configuration
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
});

API.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
);

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledWebcam = styled(Webcam)(({ theme }) => ({
  width: '100%',
  height: 'auto',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}));

const CameraContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '600px',
  margin: '0 auto',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  boxShadow: theme.shadows[8],
}));

const CameraOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.3) 100%)',
  zIndex: 2,
}));

const CaptureButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  color: theme.palette.primary.main,
  boxShadow: theme.shadows[4],
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
  width: 64,
  height: 64,
}));

const ImagePreview = styled('img')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
}));

const ConfidenceBar = styled(LinearProgress)(({ theme, value }) => ({
  height: 10,
  borderRadius: 5,
  backgroundColor: theme.palette.grey[200],
  '& .MuiLinearProgress-bar': {
    backgroundColor:
        value < 30 ? theme.palette.error.main :
            value < 70 ? theme.palette.warning.main :
                theme.palette.success.main,
  }
}));

const ProductCard = styled(Card)(({ theme, selected }) => ({
  overflow: 'visible',
  position: 'relative',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[8],
  border: selected ? `2px solid ${theme.palette.primary.main}` : 'none',
  transition: 'border 0.2s ease-in-out',
}));

const ProductBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -20,
  right: 20,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: '50%',
  width: 80,
  height: 80,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  boxShadow: theme.shadows[4],
  zIndex: 1,
}));

const CartFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  zIndex: 1000,
}));

function ProductRecognitionPage() {
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const theme = useTheme();

  const [capturedImage, setCapturedImage] = useState(null);
  const [showCamera, setShowCamera] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [scanMetadata, setScanMetadata] = useState(null);
  const [shelfAnalysis, setShelfAnalysis] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const [flashOn, setFlashOn] = useState(false);

  // New state for multi-product selection
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showBatchAddDialog, setShowBatchAddDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editedProducts, setEditedProducts] = useState({});

  const captureImage = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setCapturedImage(imageSrc);
      setShowCamera(false);
    }
  }, [webcamRef]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedImage(reader.result);
        setShowCamera(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const retakeImage = () => {
    setCapturedImage(null);
    setShowCamera(true);
    setProductResults([]);
    setScanMetadata(null);
    setShelfAnalysis(null);
    setSelectedProducts([]);
    setEditedProducts({});
    setError('');
  };

  const toggleCameraFacing = () => {
    setFacingMode(prevMode => prevMode === "user" ? "environment" : "user");
  };

  const toggleFlash = () => {
    setFlashOn(prev => !prev);
  };

  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const processImage = async () => {
    if (!capturedImage) return;

    setIsLoading(true);
    setError('');
    setProductResults([]);
    setScanMetadata(null);
    setShelfAnalysis(null);
    setSelectedProducts([]);
    setEditedProducts({});

    try {
      const formData = new FormData();
      const imageBlob = dataURItoBlob(capturedImage);
      formData.append('image', imageBlob, 'product.jpg');

      const response = await API.post('/api/inventory/recognize-product', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && Array.isArray(response.data.results.products) && response.data.results.products.length > 0) {
        const productsWithIds = response.data.results.products.map((product, index) => ({
          ...product,
          id: `product-${index}`,
          selected: false
        }));

        setProductResults(productsWithIds);
        setScanMetadata(response.data.scan_metadata);
        setShelfAnalysis(response.data.shelf_analysis);
        setNotification({
          type: 'success',
          message: `${productsWithIds.length} product(s) identified!`
        });
      } else if (response.data && Array.isArray(response.data.results.products) && response.data.results.products.length === 0) {
        setError('No products recognized in the image. Please try a different image or angle.');
      }
      else {
        setError(response.data?.message || 'Could not recognize product in the image. Please try again with a clearer image.');
      }
    } catch (error) {
      console.error('Error recognizing product:', error);
      setError(error.response?.data?.message || 'Failed to process image. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle product selection
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Handle editing a product's details
  const handleEditProduct = (product) => {
    setEditingProduct(product);

    // Initialize edited product data if not already present
    if (!editedProducts[product.id]) {
      const quantity = product.quantity?.count || 1;

      setEditedProducts(prev => ({
        ...prev,
        [product.id]: {
          quantity: quantity,
          unit_of_measure: determineUnitOfMeasure(product),
          notes: formatProductNotes(product)
        }
      }));
    }
  };

  // Save edited product details
  const saveProductEdit = () => {
    setEditingProduct(null);
  };

  // Determine the best unit of measure based on product details
  const determineUnitOfMeasure = (product) => {
    return product.quantity?.unit || 'pcs';
  };

  // Format product notes from details
  const formatProductNotes = (product) => {
    let notes = '';
    if (product.position) {
      notes += `Position: Row ${product.position.row}, Column ${product.position.column_start}-${product.position.column_end}\\n`;
    }
    if (product.warehouse_data) {
      notes += `Stock Level: ${product.warehouse_data.stock_level}\\n`;
      if (product.warehouse_data.needs_restock) {
        notes += 'Needs Restock!\\n';
      }
    }
    return notes.trim();
  };

  // Update edited product data
  const updateEditedProduct = (productId, field, value) => {
    setEditedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value
      }
    }));
  };

  // Add a single product to inventory
  const handleAddSingleProductToInventory = (product) => {
    if (!product) return;

    const productData = editedProducts[product.id] || {};

    const formData = {
      item_name: product.product_name || '',
      quantity: productData.quantity || product.quantity?.count || 1,
      unit_of_measure: productData.unit_of_measure || product.quantity?.unit || 'pcs',
      notes: productData.notes || formatProductNotes(product)
    };

    navigate('/inventory/add', { state: { prefillData: formData } });
  };

  // Add multiple products to inventory
  const handleAddMultipleProductsToInventory = () => {
    if (selectedProducts.length === 0) {
      setNotification({
        type: 'warning',
        message: 'Please select at least one product to add to inventory'
      });
      return;
    }

    const selectedProductsData = selectedProducts.map(productId => {
      const product = productResults.find(p => p.id === productId);
      const productData = editedProducts[productId] || {};

      const formData = {
        item_name: product.product_name || '',
        quantity: productData.quantity || product.quantity?.count || 1,
        unit_of_measure: productData.unit_of_measure || product.quantity?.unit || 'pcs',
        notes: productData.notes || formatProductNotes(product)
      };

      return formData;
    });

    navigate('/inventory/batch-add', {
      state: {
        products: selectedProductsData,
        returnPath: '/product-recognition'
      }
    });
  };

  // Add ALL products to inventory (without selection)
  const handleAddAllProductsToInventory = () => {
    if (productResults.length === 0) {
      setNotification({
        type: 'warning',
        message: 'No products detected to add to inventory'
      });
      return;
    }

    const allProductsData = productResults.map(product => {
      const productData = editedProducts[product.id] || {};

      const formData = {
        item_name: product.product_name || '',
        quantity: productData.quantity || product.quantity?.count || 1,
        unit_of_measure: productData.unit_of_measure || product.quantity?.unit || 'pcs',
        notes: productData.notes || formatProductNotes(product)
      };

      return formData;
    });

    navigate('/inventory/batch-add', {
      state: {
        products: allProductsData,
        returnPath: '/product-recognition'
      }
    });
  };

  const handleClose = () => {
    navigate('/inventory');
  };

  const getConfidenceColor = (confidence) => {
    const confidencePercent = (confidence || 0) * 100;
    if (confidencePercent < 30) return theme.palette.error.main;
    if (confidencePercent < 70) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const getConfidenceText = (confidence) => {
    const confidencePercent = (confidence || 0) * 100;
    if (confidencePercent >= 90) return "Very High Confidence";
    if (confidencePercent >= 70) return "High Confidence";
    if (confidencePercent >= 50) return "Medium Confidence";
    if (confidencePercent >= 30) return "Low Confidence";
    return "Very Low Confidence";
  };

  // Common units of measure for dropdown
  const commonUnits = [
    { value: 'pcs', label: 'Pieces' },
    { value: 'g', label: 'Grams' },
    { value: 'kg', label: 'Kilograms' },
    { value: 'ml', label: 'Milliliters' },
    { value: 'l', label: 'Liters' },
    { value: 'oz', label: 'Ounces' },
    { value: 'lb', label: 'Pounds' }
  ];

  return (
      <Container maxWidth="md">
        <Snackbar
            open={notification !== null}
            autoHideDuration={6000}
            onClose={() => setNotification(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            TransitionComponent={Fade}
        >
          <Alert
              onClose={() => setNotification(null)}
              severity={notification?.type || 'info'}
              variant="filled"
              sx={{ width: '100%' }}
          >
            {notification?.message}
          </Alert>
        </Snackbar>

        {/* Product Info Dialog */}
        <Dialog
            open={showInfoDialog}
            onClose={() => setShowInfoDialog(false)}
            maxWidth="sm"
            fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PsychologyIcon color="primary" />
              <Typography variant="h6">About Product Recognition</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography paragraph>
              Our product recognition feature uses AI to identify multiple products from a single image with high accuracy.
            </Typography>
            <Typography paragraph>
              For best results:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <Box component="li" sx={{ mb: 1 }}>
                Ensure products are well-lit and clearly visible
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                Arrange products so their labels are visible
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                Try to capture all products you want to identify
              </Box>
              <Box component="li">
                Hold the camera steady when taking the photo
              </Box>
            </Box>

            <Typography paragraph sx={{ mt: 2 }}>
              <strong>Understanding Confidence Scores:</strong>
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <strong>90-100%:</strong> Very high confidence - Clear product with visible labels
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <strong>70-90%:</strong> High confidence - Product is clear but some details may be missing
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <strong>50-70%:</strong> Medium confidence - Can identify product type but not specific details
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <strong>30-50%:</strong> Low confidence - Can make an educated guess
              </Box>
              <Box component="li">
                <strong>0-30%:</strong> Very low confidence - Image is unclear or product is not visible
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowInfoDialog(false)} color="primary">
              Got it
            </Button>
          </DialogActions>
        </Dialog>

        {/* Product Edit Dialog */}
        <Dialog
            open={editingProduct !== null}
            onClose={() => setEditingProduct(null)}
            maxWidth="sm"
            fullWidth
        >
          {editingProduct && (
              <>
                <DialogTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EditIcon color="primary" />
                    <Typography variant="h6">Edit Product Details</Typography>
                  </Box>
                </DialogTitle>
                <DialogContent>
                  <Typography variant="h6" gutterBottom>
                    {editingProduct.product_name}
                  </Typography>
                  {editingProduct.brand && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {editingProduct.brand}
                      </Typography>
                  )}

                  <Box sx={{ mt: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                            label="Quantity"
                            type="number"
                            fullWidth
                            value={editedProducts[editingProduct.id]?.quantity || 1}
                            onChange={(e) => updateEditedProduct(
                                editingProduct.id,
                                'quantity',
                                Math.max(1, parseInt(e.target.value, 10) || 1)
                            )}
                            InputProps={{
                              inputProps: { min: 1 }
                            }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                            select
                            label="Unit"
                            fullWidth
                            value={editedProducts[editingProduct.id]?.unit_of_measure || determineUnitOfMeasure(editingProduct)}
                            onChange={(e) => updateEditedProduct(editingProduct.id, 'unit_of_measure', e.target.value)}
                        >
                          {commonUnits.map((unit) => (
                              <MenuItem key={unit.value} value={unit.value}>
                                {unit.label}
                              </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                            label="Notes"
                            multiline
                            rows={3}
                            fullWidth
                            value={editedProducts[editingProduct.id]?.notes || formatProductNotes(editingProduct)}
                            onChange={(e) => updateEditedProduct(editingProduct.id, 'notes', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setEditingProduct(null)} color="inherit">
                    Cancel
                  </Button>
                  <Button
                      onClick={saveProductEdit}
                      color="primary"
                      variant="contained"
                      startIcon={<SaveIcon />}
                  >
                    Save Changes
                  </Button>
                </DialogActions>
              </>
          )}
        </Dialog>

        {/* Batch Add Dialog */}
        <Dialog
            open={showBatchAddDialog}
            onClose={() => setShowBatchAddDialog(false)}
            maxWidth="md"
            fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCartIcon color="primary" />
              <Typography variant="h6">Add Selected Products to Inventory</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedProducts.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No products selected. Please select products to add to your inventory.
                </Alert>
            ) : (
                <>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    You've selected {selectedProducts.length} product(s) to add to your inventory.
                  </Typography>
                  <List>
                    {selectedProducts.map(productId => {
                      const product = productResults.find(p => p.id === productId);
                      const productData = editedProducts[productId] || {};

                      return (
                          <ListItem key={productId} divider>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={6}>
                                <Typography variant="body1" fontWeight="medium">
                                  {product.product_name}
                                </Typography>
                                {product.brand && (
                                    <Typography variant="body2" color="text.secondary">
                                      {product.brand}
                                    </Typography>
                                )}
                              </Grid>
                              <Grid item xs={3}>
                                <Typography variant="body2">
                                  Quantity: {productData.quantity || 1} {productData.unit_of_measure || determineUnitOfMeasure(product)}
                                </Typography>
                              </Grid>
                              <Grid item xs={3} sx={{ textAlign: 'right' }}>
                                <Button
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => handleEditProduct(product)}
                                >
                                  Edit
                                </Button>
                              </Grid>
                            </Grid>
                          </ListItem>
                      );
                    })}
                  </List>
                </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowBatchAddDialog(false)} color="inherit">
              Cancel
            </Button>
            <Button
                onClick={handleAddMultipleProductsToInventory}
                color="primary"
                variant="contained"
                startIcon={<AddIcon />}
                disabled={selectedProducts.length === 0}
            >
              Add All to Inventory
            </Button>
          </DialogActions>
        </Dialog>

        <Box sx={{ mb: 4 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PsychologyIcon
                  sx={{
                    fontSize: 36,
                    mr: 2,
                    color: 'primary.main'
                  }}
              />
              <Typography variant="h4" component="h1" fontWeight="bold">
                Multi-Product Recognition
              </Typography>
            </Box>

            <Tooltip title="Learn about product recognition">
              <IconButton
                  color="primary"
                  onClick={() => setShowInfoDialog(true)}
                  size="small"
              >
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Take a photo of multiple products to identify them all at once
          </Typography>
        </Box>

        {error && (
            <Alert
                severity="error"
                sx={{ mb: 3 }}
                onClose={() => setError('')}
                action={
                  <Button color="inherit" size="small" onClick={retakeImage}>
                    Try Again
                  </Button>
                }
            >
              {error}
            </Alert>
        )}

        {showCamera ? (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
              <Paper
                  elevation={3}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    mb: 4,
                    overflow: 'hidden'
                  }}
              >
                <CameraContainer>
                  <StyledWebcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        facingMode: facingMode,
                        width: { ideal: 1280 },
                        height: { ideal: 720 }
                      }}
                      mirrored={facingMode === "user"}
                  />
                  <CameraOverlay>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%'
                    }}>
                      <Tooltip title="Switch camera">
                        <IconButton
                            color="inherit"
                            onClick={toggleCameraFacing}
                            sx={{ backgroundColor: 'rgba(0,0,0,0.3)', color: 'white' }}
                        >
                          <CameraSwitchIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={flashOn ? "Turn flash off" : "Turn flash on"}>
                        <IconButton
                            color="inherit"
                            onClick={toggleFlash}
                            sx={{
                              backgroundColor: 'rgba(0,0,0,0.3)',
                              opacity: 0.5, // Keep disabled look if not functional
                              color: 'white'
                            }}
                            disabled={true}
                        >
                          {flashOn ? <FlashOnIcon /> : <FlashOffIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%'
                    }}>
                      <CaptureButton
                          onClick={captureImage}
                          size="large"
                      >
                        <CameraIcon fontSize="large" />
                      </CaptureButton>
                    </Box>
                  </CameraOverlay>
                </CameraContainer>

                <Box sx={{
                  mt: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<UploadIcon />}
                      onClick={triggerFileInput}
                  >
                    Upload Image
                  </Button>

                  <Button
                      variant="outlined"
                      color="inherit"
                      startIcon={<ArrowBackIcon />}
                      onClick={handleClose}
                  >
                    Back to Inventory
                  </Button>

                  <VisuallyHiddenInput
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      ref={fileInputRef}
                  />
                </Box>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mt: 2 }}
                >
                  Position multiple products in the frame to identify them all at once
                </Typography>
              </Paper>
            </motion.div>
        ) : (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
              <Paper
                  elevation={3}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    mb: 4
                  }}
              >
                {/* Show review image if captured, not loading, and no results yet */}
                {capturedImage && (!productResults || productResults.length === 0) && !isLoading && (
                    <>
                      <Typography variant="h6" gutterBottom align="center">
                        Review Image
                      </Typography>

                      <Box sx={{
                        maxWidth: 500,
                        mx: 'auto',
                        mb: 3,
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: 4
                      }}>
                        <ImagePreview src={capturedImage} alt="Captured products" />
                      </Box>

                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        gap: 2
                      }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SearchIcon />}
                            onClick={processImage}
                        >
                          Identify Products
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={retakeImage}
                        >
                          Retake Photo
                        </Button>

                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<CloseIcon />}
                            onClick={handleClose}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </>
                )}

                {isLoading && (
                    <Box sx={{
                      textAlign: 'center',
                      py: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2
                    }}>
                      <CircularProgress size={60} thickness={4} />
                      <Typography variant="h6">
                        Analyzing the image, please wait...
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Our AI is identifying all products in your image...
                      </Typography>
                      <LinearProgress
                          sx={{ width: '80%', maxWidth: 400, mt: 2 }}
                          color="primary"
                      />
                    </Box>
                )}

                {/* Render a card for each product in productResults */}
                {productResults && productResults.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                          {productResults.length} Product{productResults.length !== 1 ? 's' : ''} Identified
                        </Typography>
                        <Box>
                          <ButtonGroup variant="contained" color="primary">
                            <Button
                                startIcon={<ShoppingCartIcon />}
                                onClick={() => setShowBatchAddDialog(true)}
                                disabled={selectedProducts.length === 0}
                            >
                              Add Selected ({selectedProducts.length})
                            </Button>
                            <Button
                                startIcon={<AddShoppingCartIcon />}
                                onClick={handleAddAllProductsToInventory}
                                color="secondary"
                            >
                              Add All Items
                            </Button>
                          </ButtonGroup>
                        </Box>
                      </Box>

                      {scanMetadata && (
                          <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                            <Typography variant="h6" gutterBottom>Scan Metadata</Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="body2">Total Items:</Typography>
                                <Typography variant="h6">{scanMetadata.total_items_counted}</Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="body2">Distinct Products:</Typography>
                                <Typography variant="h6">{scanMetadata.distinct_products}</Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography variant="body2">Shelf Utilization:</Typography>
                                <Typography variant="h6">{`${(scanMetadata.shelf_utilization * 100).toFixed(0)}%`}</Typography>
                              </Grid>
                            </Grid>
                          </Paper>
                      )}

                      {shelfAnalysis && shelfAnalysis.recommendations && shelfAnalysis.recommendations.length > 0 && (
                          <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                            <Typography variant="h6" gutterBottom>Shelf Analysis</Typography>
                            <List dense>
                              {shelfAnalysis.recommendations.map((rec, index) => (
                                  <ListItem key={index}>
                                    <ListItemText primary={rec} />
                                  </ListItem>
                              ))}
                            </List>
                          </Paper>
                      )}

                      {productResults.map((product, index) => (
                          <Zoom
                              in={true}
                              key={product.id || index}
                              style={{ transitionDelay: `${index * 100}ms` }}
                          >
                            <ProductCard selected={selectedProducts.includes(product.id)}>
                              {product.confidence && (
                                  <ProductBadge>
                                    <Typography variant="h6" fontWeight="bold">
                                      {Math.round(product.confidence * 100)}%
                                    </Typography>
                                    <Typography variant="caption">
                                      Match
                                    </Typography>
                                  </ProductBadge>
                              )}

                              <CardContent sx={{ pt: 4 }}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} sm={5} md={4}>
                                    <Box sx={{
                                      borderRadius: 2,
                                      overflow: 'hidden',
                                      boxShadow: 2,
                                      mb: { xs: 2, sm: 0 },
                                      position: 'relative'
                                    }}>
                                      <ImagePreview src={capturedImage} alt="Captured product" />
                                      <FormControlLabel
                                          control={
                                            <Checkbox
                                                checked={selectedProducts.includes(product.id)}
                                                onChange={() => toggleProductSelection(product.id)}
                                                sx={{
                                                  position: 'absolute',
                                                  top: 0,
                                                  left: 0,
                                                  backgroundColor: 'rgba(255,255,255,0.7)',
                                                  borderRadius: '0 0 8px 0',
                                                  '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                                  }
                                                }}
                                            />
                                          }
                                          label=""
                                      />
                                    </Box>
                                  </Grid>

                                  <Grid item xs={12} sm={7} md={8}>
                                    <Typography variant="h5" gutterBottom fontWeight="bold">
                                      {product.product_name || 'Unknown Product'}
                                    </Typography>

                                    {typeof product.confidence === 'number' && (
                                        <Box sx={{ mb: 2 }}>
                                          <Typography
                                              variant="body2"
                                              color="text.secondary"
                                              gutterBottom
                                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                          >
                                            Recognition Confidence
                                            <Tooltip title="Confidence level indicates how certain we are about this identification">
                                              <InfoIcon fontSize="small" sx={{ opacity: 0.7 }} />
                                            </Tooltip>
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ConfidenceBar
                                                variant="determinate"
                                                value={product.confidence * 100}
                                                sx={{ flexGrow: 1 }}
                                            />
                                            <Typography
                                                variant="body2"
                                                fontWeight="medium"
                                                sx={{
                                                  color: getConfidenceColor(product.confidence),
                                                  minWidth: 45,
                                                  textAlign: 'right'
                                                }}
                                            >
                                              {Math.round(product.confidence * 100)}%
                                            </Typography>
                                          </Box>
                                          <Typography
                                              variant="caption"
                                              sx={{
                                                color: getConfidenceColor(product.confidence),
                                                display: 'block',
                                                mt: 0.5
                                              }}
                                          >
                                            {getConfidenceText(product.confidence)}
                                          </Typography>
                                        </Box>
                                    )}

                                    <Box sx={{ mb: 2 }}>
                                      {product.quantity && (
                                          <Typography variant="body1" sx={{ mb: 1 }}>
                                            <Typography component="span" fontWeight="medium" color="text.secondary">
                                              Quantity:
                                            </Typography>{' '}
                                            {product.quantity.count} {product.quantity.unit}
                                          </Typography>
                                      )}

                                      {product.position && (
                                          <Typography variant="body1" sx={{ mb: 1 }}>
                                            <Typography component="span" fontWeight="medium" color="text.secondary">
                                              Position:
                                            </Typography>{' '}
                                            {`Row ${product.position.row}, Col ${product.position.column_start}-${product.position.column_end}`}
                                          </Typography>
                                      )}

                                      {product.warehouse_data && (
                                          <>
                                            <Typography variant="body1" sx={{ mb: 1 }}>
                                              <Typography component="span" fontWeight="medium" color="text.secondary">
                                                Stock Level:
                                              </Typography>{' '}
                                              {product.warehouse_data.stock_level}
                                            </Typography>
                                            {product.warehouse_data.needs_restock && (
                                                <Chip label="Needs Restock" color="error" size="small" />
                                            )}
                                          </>
                                      )}
                                    </Box>
                                  </Grid>
                                </Grid>
                              </CardContent>

                              <Divider />

                              <CardActions sx={{ p: 2, justifyContent: 'space-between' }}>
                                <FormControlLabel
                                    control={
                                      <Checkbox
                                          checked={selectedProducts.includes(product.id)}
                                          onChange={() => toggleProductSelection(product.id)}
                                      />
                                    }
                                    label="Select for batch add"
                                />

                                <Box>
                                  <Button
                                      variant="outlined"
                                      color="primary"
                                      startIcon={<EditIcon />}
                                      onClick={() => handleEditProduct(product)}
                                      sx={{ mr: 1 }}
                                  >
                                    Edit
                                  </Button>

                                  <Button
                                      variant="contained"
                                      color="primary"
                                      startIcon={<AddIcon />}
                                      onClick={() => handleAddSingleProductToInventory(product)}
                                  >
                                    Add to Inventory
                                  </Button>
                                </Box>
                              </CardActions>
                            </ProductCard>
                          </Zoom>
                      ))}

                      {/* Add All Items to Inventory button at the bottom */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            startIcon={<AddShoppingCartIcon />}
                            onClick={handleAddAllProductsToInventory}
                        >
                          Add All Items to Inventory
                        </Button>
                      </Box>

                      {/* Global button to rescan if multiple results are shown */}
                      <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<RefreshIcon />}
                          onClick={retakeImage}
                          sx={{ mt: 2, alignSelf: 'center' }}
                      >
                        Scan More Products
                      </Button>
                    </Box>
                )}
              </Paper>
            </motion.div>
        )}

        {/* Floating action button for batch add when products are selected */}
        {productResults && productResults.length > 0 && selectedProducts.length > 0 && (
            <CartFab
                color="primary"
                onClick={() => setShowBatchAddDialog(true)}
            >
              <Badge badgeContent={selectedProducts.length} color="error">
                <ShoppingCartIcon />
              </Badge>
            </CartFab>
        )}
      </Container>
  );
}

export default ProductRecognitionPage;